import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLogger } from 'hook/error.hook';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { isPremium } from 'helpers/userHelper';
import {
  IN_PROGRESS,
  ENROLLED,
  UNENROLLED,
  LANDLORD_DETAILS_NEEDED,
  retrieveRentReportingStatusText,
} from 'types/rentReporting.constants';
import { getRentReportingStatuses } from 'helpers/rentReporting.helper';
import { get } from 'helpers/objectHelpers';
import {
  getRentReportingPayments,
  a_postReportEnrollment,
  a_postRentReportingAuthentication,
  a_postRentRentalInfo,
  getRentReportingAuthData,
} from 'actions/rentReporting.actions';
import { openNewTab } from 'helpers/domHelper';
import usePhoneNumberInput from 'pages/Profile/pages/ProfileOverview/hooks/usePhoneNumberInput';
import useDirtyData from 'pages/Profile/pages/ProfileOverview/hooks/useDirtyData';
import { patchUser } from 'actions/userInfo.actions';
import { useViewModalWatcher } from 'hook/helper.hook';
import { wrapService } from 'actions/service_wrapper.actions';
import {
  getCMAlertByAlertId,
  getCreditMonitoringAlerts,
} from 'externals/_services/alert.service';

import { getDiffDaysBetweenDates } from 'helpers/dateHelper';
import {
  BANKING_VERTICAL,
  RENT_REPORTING_VERTICAL,
} from 'types/mrph.vertical.constants';

const a_getCreditMonitoringAlerts = wrapService(getCreditMonitoringAlerts, {
  name: 'getCreditMonitoringAlerts',
});

const a_getCMAlertByAlertId = wrapService(getCMAlertByAlertId, {
  name: 'getCMAlertByAlertId',
});

export const useRentPaymentReportHelpers = eventHandler => {
  const [disableRentalPropertiesButton, setDisableRentalPropertiesButton] =
    useState(false);
  const [disableRentalPaymentsButton, setDisableRentalPaymentsButton] =
    useState(false);
  const [disableLinkedBankAccountsButton, setDisableLinkedBankAccountsButton] =
    useState(false);
  const [disableMyDocumentsButton, setDisableMyDocumentsButton] =
    useState(false);
  const [disableGetStartedButton, setDisableGetStartedButton] = useState(false);
  const [disableRentStatusButton, setDisableRentStatusButton] = useState(false);
  const [isDisplayCMAlertMessage, setDisplayCMAlertMessage] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const logger = useLogger({
    name: 'rentPaymentReport.hook',
    categories: [`rentPaymentReport`],
  });

  const {
    rentReportingPayments,
    rentReportingPaymentsError,
    rentReportingPaymentsLoading,
    rentReportingAuthLoading,
    rentReportingAuthError,
    rentReportingAuth,
    userInfo,
  } = useSelector(state => ({
    rentReportingPayments: state.rentReporting.rentReportingPayments,
    rentReportingPaymentsError: state.rentReporting.rentReportingPaymentsError,
    rentReportingPaymentsLoading:
      state.rentReporting.rentReportingPaymentsLoading,
    rentReportingAuthLoading: state.rentReporting.rentReportingAuthLoading,
    rentReportingAuthError: state.rentReporting.rentReportingAuthError,
    rentReportingAuth: state.rentReporting.rentReportingAuth,
    userInfo: state.userInfo.userInfo,
  }));
  const enrollmentStatus = rentReportingAuth?.enrollmentStatus;
  const shouldRentReportingApiBeCalled =
    userInfo?.rentReporting &&
    isPremium(userInfo) &&
    userInfo?.rentReporting?.status !== UNENROLLED;

  const rentReportingStatus =
    userInfo && userInfo.rentReporting && enrollmentStatus
      ? enrollmentStatus['status']
      : '';
  const rentReportingSubStatus =
    userInfo && userInfo.rentReporting && enrollmentStatus
      ? enrollmentStatus['subStatus']
      : '';
  const isInProgressLDNhook =
    rentReportingStatus === IN_PROGRESS &&
    rentReportingSubStatus === LANDLORD_DETAILS_NEEDED;
  const phoneNumberInput = usePhoneNumberInput(
    get(userInfo, ['contactPhoneNumber']),
    true,
  );
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);

  useViewModalWatcher(
    { 'Pop Up Name': 'Zingo Update Phone Number' },
    showPhoneNumberModal,
  );
  useEffect(() => {
    const fetchRentReportingPayments =
      !rentReportingPayments &&
      !rentReportingPaymentsError &&
      !rentReportingPaymentsLoading &&
      shouldRentReportingApiBeCalled;

    if (userInfo?.rentReporting?.status === ENROLLED) {
      dispatch(a_getCreditMonitoringAlerts())
        .then(response => {
          if (response?.items) {
            let cmAlert = response.items
              .filter(item => {
                const days = getDiffDaysBetweenDates(
                  new Date(),
                  item?.createDate,
                );
                return (
                  days < 4 &&
                  item?.bureau === 'TRANS_UNION' &&
                  item?.category === 'New Account'
                );
              })
              .shift();
            if (cmAlert) {
              dispatch(a_getCMAlertByAlertId(cmAlert.id))
                .then(response => {
                  let isCMAlertMessage =
                    response?.alertDetails?.industryType ===
                      'Property Management' &&
                    response?.alertDetails?.entityName ===
                      'ZINGO/CREDIT SESAME';
                  setDisplayCMAlertMessage(isCMAlertMessage);
                })
                .catch(err => {
                  logger.errorException('Fetching alert details failed', err);
                });
            } else {
              logger.error('New Account alerts are not found');
            }
          } else {
            logger.error('Unknown response while fetching alerts');
          }
        })
        .catch(err => {
          logger.reportAPIError(err);
          logger.errorException('Failed fetching Credit alerts', err);
        });
    }

    if (fetchRentReportingPayments) {
      dispatch(getRentReportingPayments());
    }

    //eslint-disable-next-line
  }, [
    dispatch,
    rentReportingPayments,
    rentReportingPaymentsError,
    rentReportingPaymentsLoading,
  ]);

  const onClickAMlink = () => {
    eventHandler(CLICK, {
      'Click Type': 'Account Mix Link Click',
    });
    history.push('/factors/accountMix');
  };

  const onClickPHlink = () => {
    eventHandler(CLICK, {
      'Click Type': 'Payment History Link Click',
    });
    history.push('/factors/paymentHistory');
  };

  const onClickContactSupportlink = () => {
    eventHandler(CLICK, {
      'Click Type': 'Go to Need Help? - VOR REJECTED',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    openNewTab('https://help.creditsesame.com/hc/en-us/requests/new');
  };

  const onClickRentPaymentReporting = () => {
    eventHandler(CLICK, {
      'Click Type': 'Set Up Rent Reporting Now',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    history.push('/rentPaymentReporting');
  };

  const onClickNoThanks = () => {
    eventHandler(CLICK, {
      'Click Type': 'Start Rent Reporting at a later time',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    history.push('/dashboard');
  };

  const onClickHaveQuestion = () => {
    eventHandler(CLICK, {
      'Click Type': "Go to Sesame FAQ's page",
    });
    openNewTab(
      'https://help.creditsesame.com/hc/en-us/categories/1260802573969-Sesame-Turbo',
    );
  };

  const onClickSeeAllLatestPayments = () => {
    rentPaymentRentalInfo('PAYMENTS');
    eventHandler(CLICK, {
      'Click Type': 'Go To My Rental Payments',
      Vertical: RENT_REPORTING_VERTICAL,
    });
  };

  const onClickRentReportingPreQualFlow = () => {
    eventHandler(CLICK, {
      'Click Type': 'Enter Rent Reporting Pre-Qualification',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    history.push('/preQualRentReporting');
  };

  const triggerEventForUiClick = rentalType => {
    switch (rentalType) {
      case 'PROPERTIES':
        return eventHandler(CLICK, {
          'Click Type': 'Continue to My Rental Properties',
          Vertical: RENT_REPORTING_VERTICAL,
        });
      case 'PAYMENTS':
        return eventHandler(CLICK, {
          'Click Type': 'Continue to My Rental Payments',
          Vertical: RENT_REPORTING_VERTICAL,
        });
      case 'ACCOUNTS':
        return eventHandler(CLICK, {
          'Click Type': 'Continue to My Linked Accounts',
          Vertical: BANKING_VERTICAL,
        });
      case 'DOCUMENTS':
        return eventHandler(CLICK, {
          'Click Type': 'Continue to My Documents',
        });
      default:
        return '';
    }
  };
  const onClickRightArrowRentalProperties = () => {
    rentPaymentRentalInfo('PROPERTIES');
    triggerEventForUiClick('PROPERTIES');
  };
  const onClickRightArrowRentalPayments = () => {
    rentPaymentRentalInfo('PAYMENTS');
    triggerEventForUiClick('PAYMENTS');
  };

  const onClickRightArrowLinkedBankAccounts = () => {
    rentPaymentRentalInfo('ACCOUNTS');
    triggerEventForUiClick('ACCOUNTS');
  };

  const onClickRightArrowMyDocuments = () => {
    rentPaymentRentalInfo('DOCUMENTS');
    triggerEventForUiClick('DOCUMENTS');
  };

  const onClosePhoneNumberUpdateModal = () => {
    setShowPhoneNumberModal(false);
    phoneNumberInput.update('');
    dirtyData.processDirtyData(
      'contactPhoneNumber',
      phoneNumberInput.trimPhoneNumberUS(''),
      '',
    );
  };

  const onUpdatePhoneClick = () => {
    const contactPhoneNumber = phoneNumberInput.contactPhoneNumber.value;
    const isNewNumberNull =
      contactPhoneNumber === null || contactPhoneNumber === '';
    if (isNewNumberNull) return;
    dispatch(patchUser({ contactPhoneNumber }))
      .then(newUserInfo => {
        const isContactPhoneNumberNotNull = !(
          newUserInfo.contactPhoneNumber === null ||
          newUserInfo.contactPhoneNumber === ''
        );
        setShowPhoneNumberModal(false);
        rentPaymentEnrollment(isInProgressLDNhook, isContactPhoneNumberNotNull);
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.showError('Unable to update phone number', err.message);
      });
  };
  const dirtyData = useDirtyData({});
  function onChangePhoneNumber(e) {
    const { value } = e.target;
    if (!/^[0-9-()_ ]*$/.test(value)) {
      return false;
    } else {
      phoneNumberInput.update(value);

      dirtyData.processDirtyData(
        'contactPhoneNumber',
        phoneNumberInput.trimPhoneNumberUS(value),
        '',
      );
    }
  }

  const rentPaymentEnrollment = (isInProgressLDN, skipPhoneNullCheck) => {
    if (
      !skipPhoneNullCheck &&
      (userInfo.contactPhoneNumber === null ||
        userInfo.contactPhoneNumber === '')
    ) {
      setShowPhoneNumberModal(true);
      return;
    }
    eventHandler(CLICK, {
      'Click Type': 'Get Started at Landlord Details',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    if (isInProgressLDN) {
      rentPaymentAuthenticate();
      return;
    }

    return new Promise((resolve, reject) => {
      setDisableGetStartedButton(true);
      dispatch(a_postReportEnrollment())
        .then(res => {
          if (res && res.redirectUrl) window.location.assign(res.redirectUrl);
          else throw new Error('Unable to find redirect url');
        })
        .catch(err => {
          setDisableGetStartedButton(false);
          logger.reportAPIError(err);
          logger.showError('Unable to process this transaction', err.message);
          logger.errorException(
            'Unable to process rentPaymentEnrollment. Please try again later.',
            err,
          );
          reject(err);
        });
    });
  };

  const rentPaymentButtonTracking = props => {
    const clickTypeDescription = getRentPaymentText(props);
    eventHandler(CLICK, {
      'Click Type': clickTypeDescription['tracking'],
      Vertical: clickTypeDescription?.vertical
        ? clickTypeDescription.vertical
        : RENT_REPORTING_VERTICAL,
    });
  };

  useEffect(() => {
    const fetchRentReportingAuth =
      !rentReportingAuth &&
      !rentReportingAuthError &&
      !rentReportingAuthLoading &&
      shouldRentReportingApiBeCalled;

    if (fetchRentReportingAuth) {
      dispatch(getRentReportingAuthData());
    }
    //eslint-disable-next-line
  }, [
    dispatch,
    rentReportingAuth,
    rentReportingAuthError,
    rentReportingAuthLoading,
  ]);

  const rentPaymentAuthenticate = () => {
    return new Promise((resolve, reject) => {
      dispatch(a_postRentReportingAuthentication())
        .then(res => {
          if (res && res.redirectUrl) window.location.assign(res.redirectUrl);
          else throw new Error('Unable to find redirect url');
        })
        .catch(err => {
          logger.reportAPIError(err);
          logger.showError('Unable to process this transaction', err.message);
          logger.errorException(
            'Unable to process rentPaymentAuthenticate. Please try again later.',
            err,
          );
          reject(err);
        });
    });
  };

  const changeInfoButtonState = (rentalType, disabled) => {
    if (rentalType === 'PROPERTIES') {
      setDisableRentalPropertiesButton(disabled);
    } else if (rentalType === 'PAYMENTS') {
      setDisableRentalPaymentsButton(disabled);
    } else if (rentalType === 'ACCOUNTS') {
      setDisableLinkedBankAccountsButton(disabled);
      setDisableRentStatusButton(disabled);
    } else if (rentalType === 'DOCUMENTS') {
      setDisableMyDocumentsButton(disabled);
    }
  };

  const rentPaymentRentalInfo = rentalType => {
    changeInfoButtonState(rentalType, true);
    return new Promise((resolve, reject) => {
      dispatch(a_postRentRentalInfo(rentalType))
        .then(res => {
          if (res && res.redirectUrl) {
            window.location.assign(res.redirectUrl);
          } else {
            throw new Error('Unable to find redirect url');
          }
        })
        .catch(err => {
          logger.reportAPIError(err);
          logger.showError('Unable to process this transaction', err.message);
          logger.errorException(
            'Unable to process rentPaymentAuthenticate. Please try again later.',
            err,
          );
          reject(err);
        })
        .finally(() => changeInfoButtonState(rentalType, false));
    });
  };

  const getRentPaymentText = ({
    userInfo,
    paymentsResponse,
    isDisplayCMAlertMessage,
  }) => {
    const rentReportingStatus = getRentReportingStatuses(
      userInfo,
      paymentsResponse,
      rentReportingAuth,
      isDisplayCMAlertMessage,
    );
    return retrieveRentReportingStatusText(rentReportingStatus);
  };

  const rentPaymentReportHelper = {
    rentPaymentEnrollment,
    onClickNoThanks,
    onClickHaveQuestion,
    showPhoneNumberModal,
    rentPaymentAuthenticate,
    getRentPaymentText,
    rentPaymentRentalInfo,
    paymentsResponse: rentReportingPayments,
    paymentsError: rentReportingPaymentsError,
    paymentsLoading: rentReportingPaymentsLoading,
    authResponse: rentReportingAuth,
    authError: rentReportingAuthError,
    authLoading: rentReportingAuthLoading,
    onClickAMlink,
    onClickPHlink,
    onClickContactSupportlink,
    rentPaymentButtonTracking,
    onClickRentPaymentReporting,
    disableRentalPropertiesButton,
    setDisableRentalPropertiesButton,
    disableRentalPaymentsButton,
    setDisableRentalPaymentsButton,
    disableLinkedBankAccountsButton,
    setDisableLinkedBankAccountsButton,
    disableMyDocumentsButton,
    setDisableMyDocumentsButton,
    disableGetStartedButton,
    setDisableGetStartedButton,
    disableRentStatusButton,
    onClosePhoneNumberUpdateModal,
    onUpdatePhoneClick,
    onClickRightArrowRentalProperties,
    onClickRightArrowRentalPayments,
    onClickRightArrowLinkedBankAccounts,
    onClickRightArrowMyDocuments,
    onClickSeeAllLatestPayments,
    onClickRentReportingPreQualFlow,
    phoneNumberInput,
    onChangePhoneNumber,
    disableUpdatePhoneButton:
      phoneNumberInput.contactPhoneNumber.value === null ||
      phoneNumberInput.contactPhoneNumber.value === '' ||
      (phoneNumberInput.contactPhoneNumber.value &&
        (phoneNumberInput.contactPhoneNumber.value.length < 10 ||
          phoneNumberInput.contactPhoneNumber.error)),
    isDisplayCMAlertMessage,
    rentReportingStatus,
    rentReportingSubStatus,
  };

  return rentPaymentReportHelper;
};
